import { TZDate } from '@date-fns/tz'
import * as chrono from 'chrono-node'
import { isDate } from 'date-fns'
import { useEffect } from 'react'
import { useController } from 'react-hook-form'
import { InputProps } from '../../Input'
import { userTimezone } from './helpers'
import { TimeSelect } from './TimeSelect'
import { parseDate } from '@unimpaired/utils'

export type TimeSelectFieldProps = Omit<
  InputProps,
  'defaultValue' | 'label' | 'name'
> & {
  label?: string
  name?: string
  timezone?: string
  defaultValue?: Date | string
}

const timeParser = chrono.casual.clone()
timeParser.parsers.push({
  pattern: () => {
    // match 1-4 digits, and a/p, returning hour/minute groups
    return /^(?:(\d{1,2})(\d{2})|(\d{1,2}))(?:([ap])m?)?$/i
  },
  extract: (context, match) => {
    const hour = parseInt(match[1] ?? match[3]) ?? 0
    const minute = parseInt(match[2] ?? 0) ?? 0
    // TODO meridiem not working and I'm out of time, not sure why
    const meridiem = match[4] === 'a' ? 0 : match[4] === 'p' ? 1 : undefined
    const meridiemText = meridiem !== undefined ? match[4] + 'm' : undefined
    return context.createParsingResult(0, `${hour}:${minute}${meridiemText}`, {
      hour,
      minute,
      meridiem: meridiem,
    })
  },
})

export const TimeSelectField = ({
  name = 'time',
  defaultValue,
  timezone,
  ...props
}: TimeSelectFieldProps) => {
  const newDate = new TZDate(new Date(), timezone ?? userTimezone)

  const getDefaultDate = () => {
    if (!defaultValue) return newDate

    // This handles the natural language parser
    if (typeof defaultValue === 'string') {
      const naturalLanguageDate = timeParser.parseDate(defaultValue)
      if (isDate(naturalLanguageDate)) return naturalLanguageDate
    }

    // Note:  Called AFTER the timeParser, so we make sure not to muddle string dates
    // If the given date is a string or a number, it will run new Date() on it
    // and confirm that it is an actual working Date
    const parsedDefaultValue = parseDate(defaultValue)
    if (!parsedDefaultValue) return newDate
    return parsedDefaultValue
  }

  const defaultDate = getDefaultDate()

  const { field: date } = useController({ control: props.form.control, name })

  useEffect(() => {
    setTimeout(() => {
      date.onChange(defaultDate)
    })
  }, [])

  return (
    <TimeSelect
      {...props}
      date={date.value}
      setDate={(newDate) => date.onChange(newDate)}
      timezone={timezone ?? userTimezone}
    />
  )
}
